import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import {
  Camera,
  ArrowLeft,
  Sparkles,
  Download,
  Tv,
  Instagram,
  Trophy,
  MoreHorizontal,
  ChevronDown,
  Heart,
} from "lucide-react";
import { playChime, primeAudio } from "@/lib/chime";
import { downloadForStories } from "@/lib/storyExport";
import ReactionBar from "@/components/ReactionBar";
import { REACTIONS, getGuestId } from "@/lib/guest";
import { toast } from "sonner";

/**
 * Instagram-style vertical FEED for guests.
 * One post per row: header (avatar + name + time), photo, reactions, meta.
 */
export default function GuestGalleryPage() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [muted, setMuted] = useState(false);
  const [newIds, setNewIds] = useState(new Set());
  const [openStoryFor, setOpenStoryFor] = useState(null);
  const [heartBurst, setHeartBurst] = useState(null);
  const lastTapRef = useRef({});
  const prevIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);

  const load = async () => {
    try {
      const [ev, ph] = await Promise.all([
        api.get(`/public/events/${slug}`),
        api.get(`/public/events/${slug}/photos`),
      ]);
      setEvent(ev.data);
      const currentIds = new Set(ph.data.map((p) => p.id));
      if (!firstLoadRef.current) {
        const arrivals = ph.data
          .filter((p) => !prevIdsRef.current.has(p.id))
          .map((p) => p.id);
        if (arrivals.length > 0) {
          if (!muted) playChime();
          setNewIds((prev) => {
            const next = new Set(prev);
            arrivals.forEach((id) => next.add(id));
            return next;
          });
          setTimeout(() => {
            setNewIds((prev) => {
              const next = new Set(prev);
              arrivals.forEach((id) => next.delete(id));
              return next;
            });
          }, 4000);
        }
      }
      prevIdsRef.current = currentIds;
      firstLoadRef.current = false;
      setPhotos(ph.data);
    } catch {
      /* no-op */
    }
  };

  useEffect(() => {
    load();
    const int = setInterval(load, 5000);
    return () => clearInterval(int);
    // eslint-disable-next-line
  }, [slug, muted]);

  const shareStory = async (photo) => {
    try {
      toast.loading("Gerando imagem para Stories...", { id: "story-gen" });
      await downloadForStories(
        fileUrl(photo.storage_path),
        `story-${photo.guest_name || "foto"}.jpg`,
      );
      toast.success("Baixado! Cole no seu Story ✨", { id: "story-gen" });
    } catch {
      toast.error("Não deu para gerar. Tente de novo.", { id: "story-gen" });
    }
  };

  const updateReactionsInFeed = (photoId, counts) => {
    setPhotos((prev) =>
      prev.map((x) =>
        x.id === photoId
          ? {
              ...x,
              reactions: counts,
              reactions_total: Object.values(counts).reduce((s, n) => s + n, 0),
            }
          : x,
      ),
    );
  };

  const triggerHeart = async (photoId) => {
    setHeartBurst(photoId);
    setTimeout(() => setHeartBurst((cur) => (cur === photoId ? null : cur)), 900);
    try {
      const { data } = await api.post(
        `/public/events/${slug}/photos/${photoId}/react`,
        { emoji: "heart", guest_id: getGuestId() },
      );
      updateReactionsInFeed(photoId, data.counts || {});
    } catch {
      /* silent */
    }
  };

  const handlePhotoTap = (photoId) => {
    const now = Date.now();
    const last = lastTapRef.current[photoId] || 0;
    if (now - last < 320) {
      lastTapRef.current[photoId] = 0;
      triggerHeart(photoId);
    } else {
      lastTapRef.current[photoId] = now;
    }
  };

  const timeAgo = (iso) => {
    if (!iso) return "";
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return `agora`;
    if (diff < 3600) return `${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  const initials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    return ((parts[0][0] || "") + (parts[1]?.[0] || "")).toUpperCase();
  };

  if (!event) {
    return (
      <div className="min-h-[100dvh] text-white flex items-center justify-center bg-black">
        <div className="text-white/50">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] text-white bg-black">
      {/* Header — sticky, Instagram-style */}
      <header className="sticky top-0 z-30 glass px-4 py-3 flex items-center justify-between border-b border-white/5" data-testid="feed-header">
        <Link
          to={`/e/${slug}`}
          className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
          data-testid="gallery-back"
        >
          <ArrowLeft className="w-4 h-4" />
          <div className="flex flex-col items-start leading-tight">
            <span className="font-display font-bold text-sm">{event.name}</span>
            <span className="text-[10px] text-white/50 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] pulse-ring" />
              feed ao vivo · {photos.length} fotos
            </span>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              primeAudio();
              setMuted((m) => !m);
            }}
            className="text-xs text-white/70 hover:text-white transition-colors w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10"
            data-testid="toggle-sound"
            title={muted ? "Ativar sino" : "Silenciar"}
          >
            {muted ? "🔕" : "🔔"}
          </button>
          <Link
            to={`/e/${slug}/hall`}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
            data-testid="open-hall"
            title="Hall da Fama"
          >
            <Trophy className="w-4 h-4 text-[#FFD700]" />
          </Link>
          <Link
            to={`/e/${slug}/slideshow`}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
            data-testid="open-slideshow"
            title="Modo telão"
          >
            <Tv className="w-4 h-4 text-[#00F0FF]" />
          </Link>
        </div>
      </header>

      {/* Sticky CTA to send new photo */}
      <div className="sticky top-[57px] z-20 bg-black/95 backdrop-blur px-4 py-3 border-b border-white/5" data-testid="feed-cta-bar">
        <Link
          to={`/e/${slug}`}
          className="btn-neon w-full text-sm py-3"
          data-testid="feed-add-photo"
        >
          <Camera className="w-4 h-4" /> Mandar minha foto
        </Link>
      </div>

      {/* FEED */}
      <main className="max-w-[520px] mx-auto pb-16">
        {photos.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <Camera className="w-10 h-10 text-white/20 mx-auto mb-3" />
            <p className="text-white/60 text-sm">
              Seja o primeiro a postar!
            </p>
          </div>
        ) : (
          <AnimatePresence>
            {photos.map((p) => {
              const isNew = newIds.has(p.id);
              return (
                <motion.article
                  key={p.id}
                  layout
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  className={`border-b border-white/5 ${
                    isNew ? "bg-[#FF007F]/5" : ""
                  }`}
                  data-testid={`feed-post-${p.id}`}
                >
                  {/* Post header */}
                  <div className="flex items-center justify-between px-4 pt-4 pb-3">
                    <div className="flex items-center gap-3">
                      {p.avatar_path ? (
                        <img
                          src={fileUrl(p.avatar_path)}
                          alt={p.guest_name}
                          className={`w-9 h-9 rounded-full object-cover ${
                            isNew ? "ring-2 ring-[#FF007F]" : ""
                          }`}
                          data-testid={`avatar-${p.id}`}
                        />
                      ) : (
                        <div
                          className={`w-9 h-9 rounded-full flex items-center justify-center font-display font-bold text-sm text-white ${
                            isNew ? "ring-2 ring-[#FF007F]" : ""
                          }`}
                          style={{
                            background: `hsl(${
                              (p.guest_name || "?").charCodeAt(0) * 37 % 360
                            }, 70%, 45%)`,
                          }}
                          aria-label={p.guest_name}
                        >
                          {initials(p.guest_name)}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="text-sm font-bold truncate">
                          {p.guest_name}
                          {isNew && (
                            <span className="ml-2 text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-full bg-[#FF007F] text-white font-bold">
                              agora
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-white/40">
                          {timeAgo(p.created_at)}
                          {event.is_vip && (
                            <span className="ml-2 inline-flex items-center gap-1 text-[#FF007F]">
                              <Sparkles className="w-3 h-3" /> VIP
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() =>
                        setOpenStoryFor(openStoryFor === p.id ? null : p.id)
                      }
                      className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
                      data-testid={`post-more-${p.id}`}
                      aria-label="mais opções"
                    >
                      <MoreHorizontal className="w-4 h-4 text-white/60" />
                    </button>
                  </div>

                  {/* Photo (9:16 aspect) — double-tap to heart */}
                  <div
                    className="relative bg-black cursor-pointer select-none"
                    onClick={() => handlePhotoTap(p.id)}
                    onDoubleClick={(e) => {
                      e.preventDefault();
                      triggerHeart(p.id);
                    }}
                    data-testid={`photo-tap-area-${p.id}`}
                  >
                    <img
                      src={fileUrl(p.storage_path)}
                      alt={p.guest_name}
                      className="w-full aspect-[9/16] object-cover"
                      draggable={false}
                      data-testid={`feed-photo-${p.id}`}
                    />
                    <AnimatePresence>
                      {heartBurst === p.id && (
                        <motion.div
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: [0, 1.4, 1.2], opacity: [0, 1, 0] }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.9, times: [0, 0.35, 1] }}
                          className="absolute inset-0 flex items-center justify-center pointer-events-none"
                          data-testid={`heart-burst-${p.id}`}
                        >
                          <Heart className="w-32 h-32 fill-[#FF007F] text-[#FF007F] drop-shadow-[0_0_30px_rgba(255,0,127,0.7)]" />
                        </motion.div>
                      )}
                    </AnimatePresence>
                    <div className="absolute bottom-2 left-2 text-[10px] uppercase tracking-[0.15em] text-white/50 px-2 py-1 rounded-full bg-black/40 backdrop-blur pointer-events-none">
                      duplo toque = ❤
                    </div>
                  </div>

                  {/* Reactions bar */}
                  <div className="px-4 pt-3 pb-2">
                    <ReactionBar
                      slug={slug}
                      photo={p}
                      initialCounts={p.reactions || {}}
                      variant="compact"
                      onUpdate={({ counts }) => updateReactionsInFeed(p.id, counts)}
                    />
                  </div>

                  {/* Caption (Instagram-style: bold name + text) */}
                  {p.caption && (
                    <div
                      className="px-4 pb-2 text-sm leading-snug"
                      data-testid={`post-caption-${p.id}`}
                    >
                      <span className="font-bold mr-1.5">{p.guest_name}</span>
                      <span className="text-white/90 whitespace-pre-wrap break-words">
                        {p.caption}
                      </span>
                    </div>
                  )}

                  {/* Meta line */}
                  <div className="px-4 pb-3 flex items-center justify-between text-xs">
                    <div className="text-white/60">
                      {p.reactions_total > 0 ? (
                        <span>
                          <span className="text-white font-bold">
                            {p.reactions_total}
                          </span>{" "}
                          reaç{p.reactions_total === 1 ? "ão" : "ões"}
                        </span>
                      ) : (
                        <span className="text-white/40">Seja o primeiro a reagir</span>
                      )}
                    </div>
                    <button
                      onClick={() => shareStory(p)}
                      className="text-[#FF007F] hover:text-white transition-colors font-medium flex items-center gap-1"
                      data-testid={`share-story-${p.id}`}
                    >
                      <Instagram className="w-3.5 h-3.5" /> Stories
                    </button>
                  </div>

                  {/* More menu */}
                  <AnimatePresence>
                    {openStoryFor === p.id && (
                      <motion.div
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="mx-4 mb-3 glass rounded-xl p-3 flex flex-col gap-1"
                      >
                        <button
                          onClick={() => {
                            shareStory(p);
                            setOpenStoryFor(null);
                          }}
                          className="text-sm text-left px-3 py-2 rounded-lg hover:bg-white/5 flex items-center gap-2"
                          data-testid={`menu-story-${p.id}`}
                        >
                          <Instagram className="w-4 h-4 text-[#FF007F]" /> Baixar para
                          Stories
                        </button>
                        <a
                          href={fileUrl(p.storage_path)}
                          download={`foto-${p.guest_name || "convidado"}.jpg`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm text-left px-3 py-2 rounded-lg hover:bg-white/5 flex items-center gap-2"
                          data-testid={`menu-download-${p.id}`}
                        >
                          <Download className="w-4 h-4 text-[#00F0FF]" /> Baixar original
                        </a>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.article>
              );
            })}
          </AnimatePresence>
        )}
      </main>

      {/* Bottom sticky "new photos" toast when out of view (simplified) */}
    </div>
  );
}
